import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Row, Col, Checkbox, Popover, Select, notification, Dropdown } from 'antd'
import { CloseOutlined, CheckOutlined } from '@ant-design/icons'
import Availabilities from '../../../Components/Table/Availabilities'
import Movements from '../../../Components/Table/Movements'
import MovementsAvailabilitiesDateTimeFilter from '../../../Components/Kiosk/DateTimeFilterMovements'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import {
  setEnableMovements,
  saveAvailabilitiesFilter,
  setSize,
  setSelectedMarketplace,
  setZeroMovements,
} from '../../../Slices/Kiosk/MovementsAvailabilitiesSlice'
import { setCapitalLetter } from '../../../Helpers/StringHelpers'
import { retrieveArticleDetailMovementsAndAvailabilites } from '../../../Services/Kiosk/ArticleServices'
import {
  SizeFilterDefaultValue,
  MovementsSelectValue,
  AvailabilitiesSelectValue,
  MarketplaceDefaultValue,
  EnableMovementLog,
  CheckboxLangClass,
} from '../../../Constants/Configuration'
import { setMovementTabVisible, setAvailabilityTabVisible } from '../../../Slices/ConfigurationSlice'
import { getActiveFilterValue } from '../../../Helpers/FilterHelper'
import { retrieveSelectedArticleKey } from '../../../Helpers/ArticlesHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'

const { Option } = Select

function MovementsAndAvailabilities() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const availabilitiesFilter = useAppSelector(state => state.present.movementsAvailabilities.value.availabilitiesFilter)
  const enableMovements = useAppSelector(state => state.present.movementsAvailabilities.value.enableMovements)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const quantities = useAppSelector(state => state.present.articleDetail.articleSelected.quantities)
  const sizeSelect = useAppSelector(state => state.present.movementsAvailabilities.value.size)
  const enableAllVariant = useAppSelector(state => state.present.quantities.value.enableAllVariant)
  const enabledMarketplaces = useAppSelector(state => state.present.articleDetail.articleSelected.enabledMarketplaces)
  const selectedMarketplace = useAppSelector(state => state.present.movementsAvailabilities.value.selectedMarketplace)
  const editTableDisable = useAppSelector(state => state.present.tableOptions.editTableDisable)
  const profileValue = useAppSelector(state => state.present.configuration.profileValue)

  const movementTabVisible = useAppSelector(state => state.present.configuration.isTabMovementVisible)
  const workspaceMovementVisible = useAppSelector(state => state.present.configuration.workspaceMovementVisible)
  const availabilityTabVisible = useAppSelector(state => state.present.configuration.isTabAvailabilityVisibile)
  const workspaceAvailabilityVisible = useAppSelector(state => state.present.configuration.workspaceAvailabilityVisible)
  const movementLogVisibile = useAppSelector(state => state.present.configuration.isMovementLogVisible)
  const dataSourceMovements = useAppSelector(state => state.present.articleDetail.articleSelected.movements)

  const [popoverVisible, setPopoverVisible] = useState(false)
  const [availabilitiesCheckbox, setAvailabilitiesCheckbox] = useState(JSON.parse(JSON.stringify(availabilitiesFilter)))

  const zeroMovements = useAppSelector(state => state.present.movementsAvailabilities.value.zeroMovements)
  const isArticleSelectedAndDataFetched =
    enableMovements === AvailabilitiesSelectValue
      ? articleSelected.fetchedAvailabilites
      : articleSelected.fetchedMovements
  const disableElement = !isArticleSelectedAndDataFetched || (articleSelected && enableAllVariant)
  const enabledMarketplacesAvailable = enabledMarketplaces ? enabledMarketplaces : []

  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  //needed for rerender filter correctly
  useEffect(() => {
    setAvailabilitiesCheckbox(JSON.parse(JSON.stringify(availabilitiesFilter)))
  }, [availabilitiesFilter])

  const sizesAvailable = useMemo(() => {
    const sizes = []
    if (quantities && quantities.length !== 0) {
      //Set all size available for selected article (Always first)
      if (quantities[0] && quantities[0].sizes && quantities[0].sizes.length !== 0) {
        quantities[0].sizes.forEach(size => {
          if (sizes.filter(element => element.name === size.name).length === 0) {
            sizes.push(size)
          }
        })
      }

      sizes.sort((a, b) => a.order - b.order)
    }
    return sizes
  }, [quantities])

  const onChangeCheckbox = useCallback(
    checkedValues => {
      const currentFilters = JSON.parse(JSON.stringify(availabilitiesCheckbox))
      currentFilters.forEach((element, index) => {
        if (checkedValues.indexOf(element.value) !== -1) {
          element.active = true
        } else {
          element.active = false
        }
      })
      setAvailabilitiesCheckbox(currentFilters)
    },
    [availabilitiesCheckbox],
  )

  const handleUpdateSectionData = useCallback(() => {
    retrieveArticleDetailMovementsAndAvailabilites(articleKey, dispatch)
  }, [articleKey, dispatch])

  const setAvailabilitiesFilter = useCallback(() => {
    setPopoverVisible(false)
    dispatch(saveAvailabilitiesFilter(JSON.parse(JSON.stringify(availabilitiesCheckbox))))

    availabilitiesCheckbox.forEach(element => {
      if (element.value === EnableMovementLog && element.active) {
        handleUpdateSectionData()
      }
    })
  }, [availabilitiesCheckbox, dispatch, handleUpdateSectionData])

  const closeAvailabilitiesFilter = useCallback(() => {
    setAvailabilitiesCheckbox(JSON.parse(JSON.stringify(availabilitiesFilter)))
    setPopoverVisible(false)
  }, [availabilitiesFilter])

  const handleSizeSelectChange = useCallback(
    value => {
      dispatch(setSize(value))

      if (enableMovements === AvailabilitiesSelectValue) {
        if (value !== SizeFilterDefaultValue && selectedMarketplace !== MarketplaceDefaultValue) {
          handleUpdateSectionData()
        }
      } else {
        handleUpdateSectionData()
      }
    },
    [enableMovements, dispatch, handleUpdateSectionData, selectedMarketplace],
  )

  const handleTableChange = useCallback(
    value => {
      if (
        value === AvailabilitiesSelectValue &&
        isArticleSelectedAndDataFetched &&
        enabledMarketplacesAvailable.length === 0
      ) {
        notification.open({
          description: renderTranslation(language, TranslationKeys.marketplaceMissingWarning),
          placement: 'topRight',
          message: '',
        })
      }

      dispatch(setEnableMovements(value))

      if (value === AvailabilitiesSelectValue) {
        if (sizeSelect !== SizeFilterDefaultValue && selectedMarketplace !== MarketplaceDefaultValue) {
          handleUpdateSectionData()
        }
      } else {
        handleUpdateSectionData()
      }
    },
    [
      dispatch,
      enabledMarketplacesAvailable.length,
      language,
      sizeSelect,
      handleUpdateSectionData,
      selectedMarketplace,
      isArticleSelectedAndDataFetched,
    ],
  )

  const handleMarketplaceChange = useCallback(
    value => {
      dispatch(setSelectedMarketplace(value))

      if (enableMovements === AvailabilitiesSelectValue) {
        if (sizeSelect !== SizeFilterDefaultValue && value !== MarketplaceDefaultValue) {
          handleUpdateSectionData()
        }
      } else {
        handleUpdateSectionData()
      }
    },
    [enableMovements, dispatch, handleUpdateSectionData, sizeSelect],
  )
  const handleZeroMovementsChange = useCallback(() => {
    dispatch(setZeroMovements(!zeroMovements))
    if (enableMovements === AvailabilitiesSelectValue) {
      if (sizeSelect !== SizeFilterDefaultValue && selectedMarketplace !== MarketplaceDefaultValue) {
        handleUpdateSectionData()
      }
    } else {
      handleUpdateSectionData()
    }
  }, [enableMovements, dispatch, handleUpdateSectionData, sizeSelect, selectedMarketplace, zeroMovements])

  const handleLogFilterVisibility = useCallback(
    visible => {
      if (!editTableDisable) {
        return
      }
      if (!visible) {
        setAvailabilitiesCheckbox(JSON.parse(JSON.stringify(availabilitiesFilter)))
      }
      setPopoverVisible(visible)
    },
    [editTableDisable, availabilitiesFilter],
  )

  const handlingCheckboxContent = useMemo(
    () => (
      <Checkbox.Group
        className='availabilities-checkbox-container'
        onChange={checkedValues => {
          onChangeCheckbox(checkedValues)
        }}
        value={getActiveFilterValue(availabilitiesCheckbox)}
      >
        <Row onClick={stopPropagation}>
          {availabilitiesFilter.map(({ value, active }, index) => {
            return (
              <Col key={value} span={24}>
                {
                  <Checkbox className={'availabilities-checkbox general-checkbox'} value={value}>
                    {renderTranslation(language, TranslationKeys[CheckboxLangClass + setCapitalLetter(value)])}
                  </Checkbox>
                }
              </Col>
            )
          })}
        </Row>
      </Checkbox.Group>
    ),
    [availabilitiesFilter, onChangeCheckbox, availabilitiesCheckbox, language],
  )

  const handlingCheckboxTitle = useMemo(
    () => (
      <Row className='availabilitiesCheckboxTitle' onClick={stopPropagation}>
        <Col span={24} className='availabilities-checkbox-title'>
          <div className='availabilities-checkbox-title-head'>
            {renderTranslation(language, TranslationKeys.availabilitiesCheckboxTitle)}
            <span className='float-right'>
              <CheckOutlined
                className='availabilities-check-icon general-check-icon'
                onClick={e => {
                  e.stopPropagation()
                  setAvailabilitiesFilter()
                }}
              />
              <CloseOutlined
                className='availabilities-close-icon general-clear-icon'
                onClick={e => {
                  e.stopPropagation()
                  closeAvailabilitiesFilter()
                }}
              />
            </span>
          </div>
        </Col>
      </Row>
    ),
    [closeAvailabilitiesFilter, setAvailabilitiesFilter, language],
  )

  return ((movementTabVisible && workspaceMovementVisible) ||
    (availabilityTabVisible && workspaceAvailabilityVisible)) &&
    !enableAllVariant ? (
    <Row>
      <Col className='pt-2 pl-2' span={24}>
        <Row className='w-100'>
          {/*per riabilitare i log, non cancellare
                             {movementLogVisibile && <Col>
                                <Popover id="availabilitiesCheckboxContent" 
                                    title={handlingCheckboxTitle}
                                    content={handlingCheckboxContent}
                                    placement="bottomLeft"
                                    trigger="click"
                                    open={popoverVisible}
                                    onOpenChange={(open) => { handleLogFilterVisibility(open); }}
                                    onClick={(e) => e.stopPropagation()}>
                                        <MenuIcon className={"generic-icon menu-icon" + (!editTableDisable ? " disable" : "")}/>
                                </Popover>
                            </Col>
                            } */}
          <Col>
            <Select
              className='availabilities-select'
              value={enableMovements}
              onChange={handleTableChange}
              disabled={disableElement}
            >
              {movementTabVisible && workspaceMovementVisible && (
                <Option key={MovementsSelectValue} value={MovementsSelectValue}>
                  {renderTranslation(language, TranslationKeys.availabilitiesMovementsCheckLabel)}
                </Option>
              )}
              {availabilityTabVisible && (
                <Option key={AvailabilitiesSelectValue} value={AvailabilitiesSelectValue}>
                  {renderTranslation(language, TranslationKeys.availabilitiesAvailabilityCheckLabel)}
                </Option>
              )}
            </Select>
          </Col>
          <Col>
            <Select
              className={
                'availabilities-size-select' +
                (enableMovements === AvailabilitiesSelectValue && isArticleSelectedAndDataFetched
                  ? sizeSelect === SizeFilterDefaultValue
                    ? ' warning'
                    : ' selected'
                  : '')
              }
              value={sizeSelect}
              onChange={handleSizeSelectChange}
              disabled={disableElement}
            >
              <Option key={SizeFilterDefaultValue} value={SizeFilterDefaultValue}>
                {renderTranslation(language, TranslationKeys.filterHandlingSize) +
                  (enableMovements === AvailabilitiesSelectValue ? ' *' : '')}
              </Option>
              {sizesAvailable.map(size => {
                return (
                  <Option key={size.name} value={size.name}>
                    {size.name}
                  </Option>
                )
              })}
            </Select>
          </Col>
          {enableMovements === AvailabilitiesSelectValue ? (
            <Col className='marketplace-filter-col'>
              <Select
                className={
                  'availabilities-marketplace-select' +
                  (isArticleSelectedAndDataFetched
                    ? selectedMarketplace === MarketplaceDefaultValue ||
                      (selectedMarketplace === MarketplaceDefaultValue && enabledMarketplacesAvailable.length === 0)
                      ? ' warning'
                      : ' selected'
                    : '')
                }
                value={selectedMarketplace}
                onChange={handleMarketplaceChange}
                disabled={disableElement}
              >
                <Option value={MarketplaceDefaultValue}>
                  {renderTranslation(language, TranslationKeys.filterHandlingMarketplace) + ' *'}
                </Option>
                {enabledMarketplacesAvailable.map(element => {
                  return (
                    <Option key={element} value={element}>
                      {element}
                    </Option>
                  )
                })}
              </Select>
            </Col>
          ) : null}
          <Col md={0} lg={0} xl={0} xxl={0} flex='1 1 100%' />
          <MovementsAvailabilitiesDateTimeFilter update={handleUpdateSectionData} />
          {enableMovements === AvailabilitiesSelectValue ? (
            <Col>
              <Checkbox className={'general-checkbox'} checked={zeroMovements} onChange={handleZeroMovementsChange}>
                {renderTranslation(language, TranslationKeys.filterHandlingZeroMovements)}
              </Checkbox>
            </Col>
          ) : null}
        </Row>
      </Col>
      <Row className='pa-2'>
        {isArticleSelectedAndDataFetched ? (
          <Col span={24} className='overflow-hidden overflow-x-auto  custom-scroll'>
            {enableMovements === AvailabilitiesSelectValue ? <Availabilities /> : <Movements />}
          </Col>
        ) : null}
      </Row>
    </Row>
  ) : null
}

export default MovementsAndAvailabilities
